import { IconButton, useTheme } from "@mui/material";
import { AddButton } from "app/components/Button/AddButton";
import { FilterSelect } from "app/components/FilterSelect";
import { ThreedotIcon } from "app/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { AddOrderModalContext } from "app/containers/CommonModal/types";
import { addOrderModalActions } from "app/containers/CommonModal/redux/slice";
import {
  selectQuoteStatusLabelKey,
  selectQuotesStatusFilter,
} from "../../redux/selector";
import { quotesActions } from "../../redux/slice";
import { QuoteStatus, QuoteStatusFilter } from "../../types";
import { useStyles } from "./styles";

const STATUS_VALUES: QuoteStatus[] = [
  QuoteStatus.DRAFT,
  QuoteStatus.SENT,
  QuoteStatus.ACCEPTED,
];

const STATUS_OPTIONS: QuoteStatusFilter[] = ["ALL", ...STATUS_VALUES];

const ActionBar = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const statusFilter = useSelector(selectQuotesStatusFilter);

  const getStatusLabel = (value: QuoteStatusFilter) => {
    if (value === "ALL") {
      return t("QUOTES.STATUS_FILTER.ALL");
    }

    return t(selectQuoteStatusLabelKey(value));
  };

  const getStatusColor = (value: QuoteStatusFilter) => {
    if (value === QuoteStatus.DRAFT) {
      return theme.palette.priceQuoteStatus.draft;
    }

    if (value === QuoteStatus.SENT) {
      return theme.palette.priceQuoteStatus.sent;
    }

    if (value === QuoteStatus.ACCEPTED) {
      return theme.palette.priceQuoteStatus.accepted;
    }

    return "";
  };

  const handleStatusFilterChange = (next: QuoteStatusFilter[]) => {
    const prevHasAll = statusFilter.includes("ALL");
    const nextHasAll = next.includes("ALL");

    if (prevHasAll !== nextHasAll) {
      dispatch(
        quotesActions.setQuotesStatusFilter(
          nextHasAll ? ["ALL", ...STATUS_VALUES] : []
        )
      );
      return;
    }

    const selectedStatuses = next.filter(
      (value) => value !== "ALL"
    ) as QuoteStatus[];
    const allSelected = STATUS_VALUES.every((status) =>
      selectedStatuses.includes(status)
    );

    dispatch(
      quotesActions.setQuotesStatusFilter(
        allSelected ? ["ALL", ...selectedStatuses] : selectedStatuses
      )
    );
  };

  return (
    <div className={styles.root} dir="ltr">
      <div className={styles.actions}>
        <AddButton
          label={t("QUOTES.CREATE_QUOTE")}
          onClick={() =>
            dispatch(
              addOrderModalActions.openAddOrderModal({
                context: AddOrderModalContext.QUOTE,
              })
            )
          }
        />
        <IconButton size="small" className={styles.iconButton}>
          <ThreedotIcon />
        </IconButton>
      </div>

      <FilterSelect
        multiple
        secondaryPlainLabel
        label={t("QUOTES.STATUS_FILTER.LABEL")}
        value={statusFilter}
        options={STATUS_OPTIONS}
        getOptionLabel={getStatusLabel}
        getOptionColor={getStatusColor}
        onChange={handleStatusFilterChange}
      />
    </div>
  );
};

export default ActionBar;
